import { type ReactNode } from "react";

import { BuiFormItem } from "@btnet/blueprint-components/BuiFormItem";
import {
  FormItemLabelAlignments,
  type IBuiFormItemProps,
} from "@btnet/blueprint-components/BuiFormItem";
import { cn } from "@btnet/utilities-core";

import { BTPopover } from "../BTPopover/BTPopover";

export { FormItemLabelAlignments };

export interface IBTFormItemProps extends IBuiFormItemProps {
  /**
   * Optional helper content shown in a popover anchored to the label. When provided,
   * the label is rendered with a dotted underline to indicate more information is
   * available on hover/focus. This is `BTFormItem`'s own tooltip behavior — the
   * primitive `BuiFormItem` deliberately has no tooltip prop.
   */
  tooltip?: ReactNode;
}

/**
 * BTFormItem wraps the Blueprint `BuiFormItem` primitive, which provides the BDS
 * layout and styling for a labeled form control (label alignment, validation
 * explain icons, required indicator, etc.). It owns the app-facing behavior that
 * the primitive intentionally does not: rendering an optional `tooltip` as a
 * `BTPopover` anchored to the label.
 *
 * It is expected that inputs such as BTInput/BTSelect/BTTextArea are used as the
 * children so the layout plays nicely with other form items.
 * @example
 * <BTFormItem label="First name" labelAlign="left" htmlFor="first-name">
 *   <BTInput id="first-name" data-testid="first-name" ... />
 * </BTFormItem>
 * @example // with a helper tooltip on the label
 * <BTFormItem label="Markup" htmlFor="markup" tooltip="Applied to every line item">
 *   <BTNumberInput id="markup" ... />
 * </BTFormItem>
 */
export function BTFormItem(props: IBTFormItemProps) {
  const { className, label, tooltip, ...otherProps } = props;

  // When a tooltip is provided, anchor a BTPopover to the label text. The dotted
  // underline (via the has-tooltip class) signals that more info is available.
  // The span is made focusable (tabIndex + role="button") so keyboard users can
  // reach the trigger — BTPopover normalizes a hover trigger to ["hover", "focus"]
  // for a11y, but a bare <span> isn't in the tab order. onClick preventDefault
  // stops the enclosing antd <label htmlFor> from stealing focus to its input,
  // which on mobile would flash the popover open then immediately dismiss it.
  const labelContent = tooltip ? (
    <BTPopover content={tooltip}>
      <span
        role="button"
        tabIndex={0}
        aria-label={typeof label === "string" ? label : undefined}
        onClick={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
          }
        }}
      >
        {label}
      </span>
    </BTPopover>
  ) : (
    label
  );

  return (
    <BuiFormItem
      {...otherProps}
      label={labelContent}
      className={cn(className, { "bui-form-item-has-tooltip": tooltip })}
    />
  );
}
